import { useState, type SubmitEvent } from "react";
import type {
  AdminRole,
  AdminUser,
  CreateAdminUserInput,
  UpdateAdminUserInput,
} from "@/types/admin";
import { getAdminRole } from "@/lib/admin";
import { generateSuggestedPassword } from "@/lib/passwords";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface UserAccountDialogProps {
  mode: "create" | "edit";
  open: boolean;
  user?: AdminUser;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: CreateAdminUserInput) => Promise<void>;
  onUpdate: (input: UpdateAdminUserInput) => Promise<void>;
}

const allowPasswordManagement = process.env.NODE_ENV !== "production";

export function UserAccountDialog({
  mode,
  open,
  user,
  saving,
  onOpenChange,
  onCreate,
  onUpdate,
}: UserAccountDialogProps) {
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [role, setRole] = useState<AdminRole>(getAdminRole(user?.role));
  const [password, setPassword] = useState(mode === "create" ? generateSuggestedPassword() : "");
  const [formError, setFormError] = useState<string | null>(null);
  const showPasswordField = allowPasswordManagement;

  const submit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (mode === "create" && password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }

    if (mode === "edit" && password && password.length < 8) {
      setFormError("New password must be at least 8 characters.");
      return;
    }

    try {
      if (mode === "create") {
        await onCreate({ name, email, password, role });
      } else if (user) {
        await onUpdate({
          userId: user.id,
          name,
          email,
          role,
          password: showPasswordField ? password || undefined : undefined,
        });
      }

      onOpenChange(false);
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : "Unable to save user.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Create User" : "Edit User"}</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Create an account with an initial password."
              : showPasswordField
                ? "Update account details, role, or set a new password."
                : "Update account details and role."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {formError}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="admin-user-name">Name</Label>
            <Input
              id="admin-user-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoComplete="name"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="admin-user-email">Email</Label>
            <Input
              id="admin-user-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="admin-user-role">Role</Label>
            <Select value={role} onValueChange={(value: AdminRole) => setRole(value)}>
              <SelectTrigger id="admin-user-role" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {showPasswordField && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="admin-user-password">
                {mode === "create" ? "Password" : "New Password"}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="admin-user-password"
                  type={mode === "create" ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required={mode === "create"}
                  minLength={8}
                  autoComplete={mode === "create" ? "new-password" : "off"}
                  placeholder={mode === "edit" ? "Leave blank to keep current password" : undefined}
                />
                {mode === "create" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPassword(generateSuggestedPassword())}
                  >
                    Suggest
                  </Button>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : mode === "create" ? "Create User" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
